import type { BayLabel } from "../SortingLabelPrintModal";

// Turn a high/low racking location row into the label the Sorting slot-label
// designs draw (Warehouse › Locations → Print labels). The designs were built
// for a sorting bay — a letter block, a number block, the barcode — and the
// racking is described the same way for the picker: a LOW location's
// bay_position ("A1".."E6") is the slot on the bay diagram the picker is
// shown, so that is what its label says. A HIGH location has no bay slot, so
// its two blocks carry the level and the bay instead. The large-value designs
// print the location's display name (falling back to the barcode), exactly as
// the per-row print on the same page does.

export interface RackingLocation {
    barcode: string;
    warehouse: string;
    display_name: string | null;
    aisle: string | null;
    bay: string | null;
    level: string | null;
    high_low: "high" | "low" | null;
    bay_position: string | null;
}

export interface LabelPalette {
    shelf: Record<string, string>;
    position: Record<string, string>;
}

// Same defaults as SortingBayManager (mirrors .letter-A … .letter-E and
// .number-1 … .number-6 in zonestorage.css), so a racking label with slot
// A1 matches the sorting label for A1 and the picker's bay diagram. The
// live palette from /pick-and-sort-admin/colours overrides these.
export const DEFAULT_LABEL_PALETTE: LabelPalette = {
    shelf: { A: "#3C89F0", B: "#3CB44B", C: "#FFE119", D: "#F58231", E: "#911EB4" },
    position: { "1": "#808080", "2": "#000000", "3": "#A67C52", "4": "#BFEF45", "5": "#42D4F4", "6": "#F032E6" },
};

// Number colours repeat every 6 (see SortingBayManager.positionColourKey).
const POSITION_COLOUR_CYCLE = 6;
export const positionColourKey = (position: number) => String(((position - 1) % POSITION_COLOUR_CYCLE) + 1);

/** Racking = a location with a high/low mark. Lanes, areas and pallets have
 *  none, and their labels come from the per-row print instead. */
export const isRackingLocation = (l: Pick<RackingLocation, "high_low">): boolean =>
    l.high_low === "high" || l.high_low === "low";

const SLOT = /^([A-Z])(\d{1,2})$/;

/** What the letter block and the number block say for this location. */
export function rackingSlot(l: RackingLocation): { shelf: string; position: number | string } {
    const m = SLOT.exec((l.bay_position ?? "").trim().toUpperCase());
    if (m) return { shelf: m[1], position: Number(m[2]) };
    return { shelf: (l.level ?? "").trim(), position: (l.bay ?? "").trim() };
}

export function rackingLabel(l: RackingLocation, palette: LabelPalette = DEFAULT_LABEL_PALETTE): BayLabel {
    const { shelf, position } = rackingSlot(l);
    const posNum = Number(position);
    return {
        barcode: l.barcode,
        label: (l.display_name && l.display_name.trim()) || l.barcode,
        shelf,
        position,
        group: l.high_low,
        aisle: (l.aisle ?? "").trim(),
        bay: (l.bay ?? "").trim(),
        level: (l.level ?? "").trim(),
        shelf_colour: palette.shelf[shelf.toUpperCase()] ?? null,
        position_colour: Number.isInteger(posNum) && posNum >= 1
            ? palette.position[positionColourKey(posNum)] ?? null
            : null,
    };
}

/** The one warehouse a print run is for. The label stock size the dialog
 *  saves is per warehouse, so a run must not straddle two: the warehouse
 *  filter if set, else the sole warehouse in the rows, else null. */
export function printWarehouse(rows: Array<Pick<RackingLocation, "warehouse">>, filter: string): string | null {
    if (filter) return filter;
    const seen = new Set(rows.map(r => r.warehouse).filter(Boolean));
    return seen.size === 1 ? Array.from(seen)[0] : null;
}

/** Per-warehouse racking label stock from a /settings/{warehouse} payload.
 *  null = the built-in 185 × 35mm standard slot label. */
export function readRackingLabelSize(d: Record<string, unknown>): { w: number | null; h: number | null } {
    return {
        w: Number(d.racking_label_width_mm) > 0 ? Number(d.racking_label_width_mm) : null,
        h: Number(d.racking_label_height_mm) > 0 ? Number(d.racking_label_height_mm) : null,
    };
}
